import { useCallback, useState, useEffect } from 'react';
import {
  EditOutlined,
  HomeOutlined,
  PlusCircleOutlined,
  RedoOutlined,
} from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import { IAddress } from '../../shared/interfaces';
import { Table } from 'antd';
import {
  DropdownOptions,
  MenuOptions,
  PageIntro,
  PageLayout,
  PagePanel,
  StatusActive,
} from '../../components';
import { useToast } from '../../hooks/toast';
import AddressCreateEdit from './CreateEdit';
import { getAddress } from '../../http';
import { useAuth } from '../../hooks/auth';
import { PermissionActions } from '../../shared/enums/permissions.enum';

const Addresses: React.FC = () => {
  const { addToast } = useToast();
  const { permissionFind } = useAuth();

  const { client_id } = useParams();
  const [search, setSearch] = useState<string>();
  const [name, setName] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [addresses, setAddresses] = useState<IAddress[]>([]);
  const [address, setAddress] = useState<IAddress | null>(null);
  const [modalCreate, setModalCreate] = useState<boolean>(false);

  const list = useCallback(async () => {
    setLoading(true);
    setAddresses([]);
    try {
      const response = await getAddress(client_id);
      setAddresses(response.data);
      setName(response.client_name);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Erro ao listar planos de contas',
      });
    }
    setLoading(false);
  }, [client_id]);

  const renderModalAddressCreateEdit = () => {
    if (modalCreate)
      return (
        <AddressCreateEdit
          open={modalCreate}
          close={() => setModalCreate(false)}
          addressObj={address}
          setAddresses={setAddresses}
          client_id={client_id}
        />
      );
  };

  useEffect(() => {
    list();
  }, []);

  return (
    <PageLayout>
      <PageIntro
        badge="Cadastros"
        icon={<HomeOutlined />}
        title={`Endereços: ${name ?? ''}`}
        description="Gerencie os endereços vinculados ao paciente em uma estrutura visual consistente com as demais telas administrativas."
      />
      <MenuOptions
        menu_items={[
          {
            label: 'Atualizar',
            key: '1',
            icon: <RedoOutlined />,
            onClick: () => list(),
          },
          permissionFind([PermissionActions.clientsUpdate])
            ? {
                label: 'Cadastrar',
                key: '2',
                icon: <PlusCircleOutlined />,
                onClick: () => {
                  setAddress(null);
                  setModalCreate(true);
                },
              }
            : null,
        ]}
        showButtonBack
        showInputSearch
        search={search}
        setSearch={setSearch}
      />
      <PagePanel>
        <Table
          scroll={{ y: 510 }}
          columns={[
            {
              title: 'CEP',
              dataIndex: 'cep',
              width: 110,
            },
            {
              title: 'Logradouro',
              dataIndex: 'address',
              width: 150,
            },
            {
              title: 'Nº',
              dataIndex: 'number',
              width: 70,
            },
            {
              title: 'Bairro',
              dataIndex: 'neighborhood',
              width: 180,
            },
            {
              title: 'Cidade',
              dataIndex: 'city',
              width: 180,
            },
            {
              title: 'UF',
              dataIndex: 'state',
              width: 60,
            },
            {
              title: 'Status',
              dataIndex: 'status',
              width: 80,
              render: (value: boolean) => <StatusActive status={value} />,
            },
            {
              title: 'Opções',
              width: 130,
              render: (cell: IAddress) => (
                <DropdownOptions
                  items={[
                    permissionFind([PermissionActions.clientsUpdate])
                      ? {
                          label: 'Editar',
                          key: '1',
                          icon: <EditOutlined />,
                          onClick: () => {
                            setAddress(cell);
                            setModalCreate(true);
                          },
                        }
                      : null,
                  ]}
                />
              ),
            },
          ]}
          loading={loading}
          dataSource={addresses}
          rowKey="id"
        />
      </PagePanel>
      {renderModalAddressCreateEdit()}
    </PageLayout>
  );
};

export default Addresses;
